import { cn } from '@commenda/utils';
import React from 'react';

import { Table, TableBody as TBody } from '../Table';
import { DATA_GRID_DEFAULT_LOADING_ROWS } from './constants';
import {
  DataGridBody,
  DataGridEmptyState,
  DataGridErrorState,
  DataGridHeader,
  DataGridLoadingState,
  DataGridToolbar,
} from './internal';
import {
  DataGridClassNames,
  DataGridColumnNode,
  DataGridDensityConfig,
  DataGridExpansionConfig,
  DataGridResizeConfig,
  DataGridRowHeaderConfig,
  DataGridRowsConfig,
  DataGridScrollConfig,
  DataGridSelectionConfig,
  DataGridSortConfig,
  DataGridStatesConfig,
  DataGridSummaryConfig,
  DataGridToolbarConfig,
} from './types';
import { useDataGrid } from './useDataGrid';
import { useDataGridHorizontalScroll } from './useDataGridHorizontalScroll';

export interface DataGridProps<TRow>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  data: TRow[];
  columns: DataGridColumnNode<TRow>[];
  rowHeaderConfig: DataGridRowHeaderConfig<TRow>;
  rowsConfig: DataGridRowsConfig<TRow>;
  expansionConfig?: DataGridExpansionConfig;
  scrollConfig?: DataGridScrollConfig;
  toolbarConfig?: DataGridToolbarConfig;
  statesConfig?: DataGridStatesConfig;
  classNames?: DataGridClassNames;
  onRowClick?: (row: TRow) => void;
}

export interface DataGridDeferredProps<TRow> {
  densityConfig?: DataGridDensityConfig;
  sortConfig?: DataGridSortConfig;
  selectionConfig?: DataGridSelectionConfig;
  summaryConfig?: DataGridSummaryConfig<TRow>;
  resizeConfig?: DataGridResizeConfig;
}

function DataGridInner<TRow>(
  props: DataGridProps<TRow>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const {
    data,
    columns,
    rowHeaderConfig,
    rowsConfig,
    expansionConfig,
    scrollConfig,
    toolbarConfig,
    statesConfig,
    classNames,
    onRowClick,
    className,
    ...rest
  } = props;

  const { table, leafColumnsById, groupColumnsById } = useDataGrid({
    data,
    columns,
    rowHeaderConfig,
    rowsConfig,
    expansionConfig,
  });

  const isRowHeaderPinned = rowHeaderConfig.isPinned !== false;
  const isHeaderSticky = scrollConfig?.isHeaderSticky ?? true;
  const isPinnedShadowEnabled =
    isRowHeaderPinned && (scrollConfig?.showPinnedColumnShadow ?? true);
  const { scrollerRef, isScrolledHorizontally } = useDataGridHorizontalScroll(
    isPinnedShadowEnabled
  );
  const showPinnedColumnShadow =
    isPinnedShadowEnabled && isScrolledHorizontally;

  const { rows } = table.getRowModel();
  const leafColumns = table.getVisibleLeafColumns();
  const hasColumns = columns.length > 0;

  const columnWidths = leafColumns.map((leafColumn) => leafColumn.getSize());

  const isLoading = statesConfig?.isLoading === true;
  const errorConfig =
    statesConfig?.isError === true ? statesConfig.errorConfig : undefined;

  const statePanel = isLoading ? null : errorConfig ? (
    <DataGridErrorState config={errorConfig} />
  ) : rows.length === 0 || !hasColumns ? (
    <DataGridEmptyState
      config={
        hasColumns
          ? statesConfig?.emptyState
          : statesConfig?.noColumnsState ?? statesConfig?.emptyState
      }
    />
  ) : null;

  const body =
    isLoading ? (
      <DataGridLoadingState
        columnWidths={columnWidths}
        rowCount={statesConfig.loadingRowCount ?? DATA_GRID_DEFAULT_LOADING_ROWS}
        classNames={classNames}
      />
    ) : statePanel ? (
      <TBody className={cn('divide-y-0', classNames?.body)} />
    ) : (
      <DataGridBody
        rows={rows}
        rowHeaderConfig={rowHeaderConfig}
        leafColumnsById={leafColumnsById}
        expansionConfig={expansionConfig}
        isRowHeaderPinned={isRowHeaderPinned}
        showPinnedColumnShadow={showPinnedColumnShadow}
        onRowClick={onRowClick}
        classNames={classNames}
      />
    );

  return (
    <div
      ref={ref}
      className={cn('flex min-h-0 flex-col', classNames?.container, className)}
      {...rest}
    >
      {toolbarConfig?.isEnabled ? (
        toolbarConfig.render ? (
          toolbarConfig.render()
        ) : (
          <DataGridToolbar
            config={toolbarConfig}
            className={classNames?.toolbar}
          />
        )
      ) : null}

      <div className="relative overflow-hidden rounded-xl border-1/2 border-border-secondary bg-bg-primary">
        <div
          ref={scrollerRef}
          className={cn('overflow-x-auto', classNames?.scroller)}
        >
          <Table
            disableContainer
            className="w-max min-w-0 table-auto border-separate border-spacing-0 divide-y-0"
          >
            <DataGridHeader
              table={table}
              rowHeaderConfig={rowHeaderConfig}
              leafColumnsById={leafColumnsById}
              groupColumnsById={groupColumnsById}
              isSticky={isHeaderSticky}
              isRowHeaderPinned={isRowHeaderPinned}
              showPinnedColumnShadow={showPinnedColumnShadow}
              classNames={classNames}
            />
            {body}
          </Table>
          {statePanel}
        </div>
      </div>
    </div>
  );
}

const DataGrid = React.forwardRef(DataGridInner) as <TRow>(
  props: DataGridProps<TRow> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;

export { DataGrid };
